import {
  isDominoGetBookingParamsReport,
  isDominoGetSalesReport,
  isDominoGetSettlementTypesReport,
} from 'apiConnector/secondary-market/reports';
import type { ClientOrRelativesResponse, Sales } from 'apiConnector/secondary-market/shared/clients';
import type { ReferentialFormResponse } from 'apiConnector/secondary-market/shared/referential';
import type { Action } from 'state/actions';
import type { TradeAuthorization } from 'state/secondaryMarket/secondaryMarket.model';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type {
  ReferentialData,
  ReferentialExtraData,
  ReferentialMap,
  Settlement,
} from 'state/secondaryMarket/shared/referential/referential.model';
import { initialReferentialMap } from 'state/secondaryMarket/shared/referential/referential.model';
import { addOrPatchKey, addOrReplaceKey } from 'state/utils/collection';

export function referentialReducerFactory<
  ActionType extends Exclude<OrderActionType, 'CP_ORDER'>,
  LoadFormResponse extends Action & {
    type: `SECONDARY_MARKET_${ActionType}_RELOAD_FORM_FETCHED` | `SECONDARY_MARKET_${ActionType}_INIT_FORM_FETCHED`;
  },
>(actionType: ActionType, onFormFetched: (response: LoadFormResponse) => Partial<ReferentialData>) {
  return (state: ReferentialMap = initialReferentialMap(), action: Action) => {
    switch (action.type) {
      case `SECONDARY_MARKET_${actionType}_INIT_FORM_FETCHED` as const:
      case `SECONDARY_MARKET_${actionType}_RELOAD_FORM_FETCHED` as const:
        const settlementError = action.response.errors?.find(isDominoGetSettlementTypesReport);
        return addOrReplaceKey(state, action.tabId, {
          ...mapInitResponse(action.response?.referential!),
          clientLoadingStatus: 'success' as const,
          settlementLoadingStatus: settlementError ? ('error' as const) : ('success' as const),
          failedToLoadSalesFromDomino: action.response.warnings?.some(isDominoGetSalesReport),
          errors: action.response.errors?.filter(isDominoGetBookingParamsReport).map(e => e.payload) ?? [],
          warnings: action.response.warnings?.filter(isDominoGetBookingParamsReport).map(e => e.payload) ?? [],
          infos: action.response.infos?.filter(isDominoGetBookingParamsReport).map(e => e.payload) ?? [],
          settlementError:
            settlementError && settlementError.payload === 'Unknown counterpart.'
              ? { severity: 'Failure', translationKey: 'unknownCounterPart' }
              : undefined,
          ...onFormFetched(action as LoadFormResponse),
        } as ReferentialData);
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_FETCH_REQUESTED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          clientLoadingStatus: 'loading' as const,
        }));
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_FAILED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          clientLoadingStatus: 'error' as const,
          sales: [],
        }));
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_RECEIVED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          ...mapClientRelatedResponse(action.response),
          clientLoadingStatus: 'success' as const,
          failedToLoadSalesFromDomino: action.response.warnings?.some(isDominoGetSalesReport),
        }));
      case `SECONDARY_MARKET_${actionType}_CUSTODIAN_RELATED_FETCH_REQUESTED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          settlementLoadingStatus: 'loading' as const,
          settlementError: undefined,
        }));
      case `SECONDARY_MARKET_${actionType}_CUSTODIAN_RELATED_RECEIVED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          settlements: action.response.settlements?.map(s => ({ ...s }) as Settlement) ?? undefined,
          settlementLoadingStatus: 'success' as const,
          settlementError: undefined,
        }));
      case `SECONDARY_MARKET_${actionType}_CUSTODIAN_RELATED_FAILED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          settlementLoadingStatus: 'error' as const,
          settlementError: {
            translationKey: action.message === 'Unknown counterpart.' ? 'unknownCounterPart' : undefined,
            severity: 'Failure',
          } satisfies SecondaryMarketFormError,
        }));
      default:
        return state;
    }
  };
}

function mapInitResponse(referential: ReferentialFormResponse): ReferentialData | undefined {
  const commonData: ReferentialExtraData = {
    clientLabel: referential?.clientLabel ?? undefined,
    isin: referential?.isin ?? undefined,
    fileCode: referential?.fileCode ?? undefined,
    maturityDate: referential?.maturityDate ?? undefined,
    sales: referential.sales?.map(s => s as Sales),
    firstSettlementDate: referential.firstSettlementDate ?? undefined,
    settlements:
      referential.settlements?.map(
        ({ description, isDefaultBuy, isDefaultSell, marketType, settlementType }) =>
          ({
            description: description ?? undefined,
            isDefaultBuy: isDefaultBuy ?? undefined,
            isDefaultSell: isDefaultSell ?? undefined,
            marketType: marketType ?? undefined,
            settlementType: settlementType ?? undefined,
          }) as Settlement,
      ) ?? [],
    settlementHolidays: referential.settlementHolidays?.map(h => h) ?? [],
    quotationType: referential?.quotationType,
    currency: referential?.currency ?? undefined,
    denomination: referential?.denomination ?? undefined,
    traderGroup: referential?.traderGroup ?? undefined,
    priceType: referential?.priceType ?? undefined,
    traderLabel: referential?.traderLabel ?? undefined,
    productName: referential?.name ?? undefined,
    tradeAuthorization: referential?.tradeAuthorization as TradeAuthorization,
    portfolio: referential?.portfolio ?? undefined,
    ric: referential?.ric ?? undefined,
    ask: referential?.ask ?? undefined,
    bid: referential?.bid ?? undefined,
    traderMessage: referential?.traderMessage ?? undefined,
    salesMessage: referential?.salesMessage ?? undefined,
    ...referential?.quantityInfo,
  };
  if (referential?.isOtc === false) {
    return {
      isOtc: false,
      clients: referential.clients,
      ...commonData,
    } as ReferentialData;
  }
  if (referential?.isOtc === true) {
    return {
      isOtc: true,
      otcClients: referential.otcClients,
      ...commonData,
    } as ReferentialData;
  }
  return undefined;
}

function mapClientRelatedResponse(response: ClientOrRelativesResponse): Partial<ReferentialData> {
  return {
    clientLabel: response.client?.label ?? undefined,
    sales: response.sales?.map(s => s as Sales),
    settlements: response.settlements?.map(s => s as Settlement),
  };
}
